import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { useClientAuth } from "@/lib/clientAuth";
import { ArrowUpRight, Loader2, KeyRound, MailCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientForgotPassword() {
    const { client, loading } = useClientAuth();
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (client) return <Navigate to="/portal" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error("Enter your email");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/clients/forgot-password`, { email });
            setSent(true);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Request failed";
            toast.error(typeof msg === "string" ? msg : "Request failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main
            data-testid="forgot-password-page"
            className="min-h-screen bg-ink text-bone flex items-center justify-center px-6 grain"
        >
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-3 mb-12">
                    <img src="/logo.png" alt="Aximo Studios" className="h-12 w-auto" />
                </Link>
                <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-ember mb-6">
                    <KeyRound className="w-3.5 h-3.5" />
                    Reset password
                </div>

                {sent ? (
                    <div
                        data-testid="forgot-success"
                        className="border border-ember/40 bg-ember/5 p-8"
                    >
                        <MailCheck className="w-8 h-8 text-ember mb-4" />
                        <h2 className="font-display text-3xl tracking-tightest text-bone mb-3">
                            Check your inbox.
                        </h2>
                        <p className="text-sm text-bone-500 leading-relaxed mb-6">
                            If an account exists for{" "}
                            <span className="text-bone">{email}</span>, we've sent a
                            secure reset link. It expires in two hours.
                        </p>
                        <Link
                            to="/portal/login"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-ember hover:text-bone transition-colors"
                        >
                            Back to sign in <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                ) : (
                    <>
                        <h1 className="font-display text-bone text-5xl tracking-tightest leading-[1] mb-10">
                            Forgot your
                            <br />
                            <span className="font-serif-i italic text-ember">
                                password?
                            </span>
                        </h1>
                        <form
                            onSubmit={onSubmit}
                            className="space-y-6 border border-white/10 bg-ink-800/40 p-8"
                            data-testid="forgot-form"
                        >
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                                    Email
                                </span>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="forgot-email"
                                    placeholder="you@company.com"
                                    autoFocus
                                    className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-3 text-bone placeholder:text-bone-500 font-display text-lg tracking-tight transition-colors"
                                />
                            </label>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="forgot-submit"
                                className="btn-ember w-full inline-flex items-center justify-between h-12 px-5 text-sm font-medium tracking-wide disabled:opacity-60"
                            >
                                {busy ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <span>Send reset link</span>
                                )}
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                        </form>
                        <p className="mt-8 text-xs text-bone-500">
                            <Link
                                to="/portal/login"
                                className="text-ember hover:text-bone transition-colors"
                            >
                                ← Back to sign in
                            </Link>
                        </p>
                    </>
                )}
            </div>
        </main>
    );
}
